import React, { useState, useEffect, useRef } from 'react';
import { Upload, FileUp, Trash2, Check, Loader2, FileText, Palette, AlertCircle } from 'lucide-react';
import { getBackendUrl } from '../utils/api';

const API_BASE = getBackendUrl() || 'http://localhost:5000';

/**
 * PPTXTemplateGallery - Upload and select PPTX templates
 * Allows users to upload designed PPTX files that can be populated with content
 */
export function PPTXTemplateGallery({ selectedTemplate, onSelectTemplate, onClose }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  // Fetch existing templates on mount
  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/templates`);
      const data = await response.json();

      if (data.templates) {
        setTemplates(data.templates);
      }
    } catch (err) {
      console.error('Failed to fetch templates:', err);
      setError('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.pptx')) {
      setError('Please select a .pptx file');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', file.name.replace('.pptx', ''));

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const response = await fetch(`${API_BASE}/api/templates/upload`, {
        method: 'POST',
        body: formData,
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      const data = await response.json();

      if (data.success && data.template) {
        setTemplates(prev => [data.template, ...prev]);
        // Auto-select the newly uploaded template
        onSelectTemplate(data.template);
      } else {
        throw new Error(data.error || 'Upload failed');
      }
    } catch (err) {
      console.error('Upload failed:', err);
      setError(err.message || 'Failed to upload template');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteTemplate = async (templateId, e) => {
    e.stopPropagation();

    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/templates/${templateId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setTemplates(prev => prev.filter(t => t.id !== templateId));
        if (selectedTemplate?.id === templateId) {
          onSelectTemplate(null);
        }
      }
    } catch (err) {
      console.error('Failed to delete template:', err);
      setError('Failed to delete template');
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-blue-500" />
          <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
            Your PPTX Templates
          </h3>
        </div>
        <span className="text-sm text-slate-500 dark:text-slate-400">
          {templates.length} template{templates.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Upload Area */}
      <div
        onClick={() => !uploading && fileInputRef.current?.click()}
        className={`
          relative border-2 border-dashed rounded-xl p-6 text-center cursor-pointer
          transition-all duration-200
          ${uploading
            ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20'
            : 'border-slate-300 dark:border-slate-600 hover:border-blue-400 hover:bg-blue-50/50 dark:hover:bg-blue-900/10'
          }
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".pptx"
          onChange={handleFileSelect}
          className="hidden"
        />

        {uploading ? (
          <div className="space-y-3">
            <Loader2 className="h-8 w-8 mx-auto text-blue-500 animate-spin" />
            <p className="text-sm text-blue-600 dark:text-blue-400">
              Uploading and parsing template...
            </p>
            <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2 max-w-xs mx-auto">
              <div
                className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center justify-center gap-2">
              <Upload className="h-6 w-6 text-slate-400" />
              <FileUp className="h-6 w-6 text-slate-400" />
            </div>
            <p className="text-sm font-medium text-slate-600 dark:text-slate-300">
              Drop a PPTX file here or click to upload
            </p>
            <p className="text-xs text-slate-400 dark:text-slate-500">
              Upload your designed PowerPoint template. AI will populate it with your content.
            </p>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <AlertCircle className="h-4 w-4 text-red-500" />
          <span className="text-sm text-red-600 dark:text-red-400">{error}</span>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-400 hover:text-red-600"
          >
            ×
          </button>
        </div>
      )}

      {/* Templates Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 text-slate-400 animate-spin" />
        </div>
      ) : templates.length === 0 ? (
        <div className="text-center py-12 text-slate-500 dark:text-slate-400">
          <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p className="text-sm">No templates uploaded yet</p>
          <p className="text-xs mt-1">Upload a designed PPTX file to get started</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              isSelected={selectedTemplate?.id === template.id}
              onSelect={() => onSelectTemplate(template)}
              onDelete={(e) => handleDeleteTemplate(template.id, e)}
            />
          ))}
        </div>
      )}

      {/* Selection Info */}
      {selectedTemplate && (
        <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="flex items-center gap-2">
            <Check className="h-4 w-4 text-blue-500" />
            <span className="text-sm text-blue-700 dark:text-blue-300">
              <strong>{selectedTemplate.name}</strong> selected -
              Your content will be populated into this template!
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Individual template card
 */
function TemplateCard({ template, isSelected, onSelect, onDelete }) {
  return (
    <div
      onClick={onSelect}
      className={`
        relative cursor-pointer rounded-xl overflow-hidden transition-all duration-200
        ${isSelected
          ? 'ring-2 ring-blue-500 shadow-lg'
          : 'border border-slate-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-600 hover:shadow-md'
        }
      `}
    >
      {/* Selection indicator */}
      {isSelected && (
        <div className="absolute top-2 right-2 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center shadow-md z-10">
          <Check className="h-4 w-4 text-white" />
        </div>
      )}

      {/* Delete button */}
      <button
        onClick={onDelete}
        className="absolute top-2 left-2 w-6 h-6 bg-red-500/80 hover:bg-red-500 rounded-full flex items-center justify-center shadow-md z-10 opacity-0 group-hover:opacity-100 hover:opacity-100 transition-opacity"
        title="Delete template"
      >
        <Trash2 className="h-3 w-3 text-white" />
      </button>

      {/* Thumbnail */}
      <div className="aspect-video bg-slate-100 dark:bg-slate-800">
        {template.thumbnailUrl || template.thumbnail_url ? (
          <img
            src={template.thumbnailUrl || template.thumbnail_url}
            alt={template.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <FileText className="h-12 w-12 text-slate-300 dark:text-slate-600" />
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-3 bg-white dark:bg-slate-800">
        <h4 className="font-semibold text-slate-800 dark:text-slate-100 text-sm truncate">
          {template.name}
        </h4>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
          {template.slideCount || template.slide_count || '?'} slides
        </p>

        {/* Color palette preview */}
        {template.colors && template.colors.length > 0 && (
          <div className="flex gap-1 mt-2">
            {template.colors.slice(0, 5).map((color, idx) => (
              <div
                key={idx}
                className="w-4 h-4 rounded-full shadow-sm border border-slate-200 dark:border-slate-600"
                style={{ backgroundColor: color }}
                title={color}
              />
            ))}
            {template.colors.length > 5 && (
              <span className="text-xs text-slate-400 ml-1">
                +{template.colors.length - 5}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default PPTXTemplateGallery;
